export enum BridgeMessageFromType {
  Init = 'init',
  ReferralCodeUpdated = 'referral-code-updated',
  Push = 'push',
  PrepareLogin = 'prepare-login',
  RefreshFinished = 'refresh-finished',
  Hiding = 'hiding',
  NativeFinished = 'native-finished',
  NativeError = 'native-error',
  LocalStorageResult = 'local-storage-result',
  HttpResponse = 'http-response',
  HttpError = 'http-error',
  LoginCompleteAck = 'login-complete-ack',
  RegistrationCompleteAck = 'registration-complete-ack',
  CompleteProfileCompleteAck = 'complete-profile-complete-ack',
  KeyboardWillShow = 'keyboard-will-show',
  KeyboardWillHide = 'keyboard-will-hide',
  LiquidGlassTabTapped = 'liquid-glass-tab-tapped',
  SigninResult = 'signin-result',
  ValidateOTPResult = 'validate-otp-result',
  ResendOTPResult = 'resend-otp-result',
  OpenNativeModalAck = 'open-native-modal-ack',
  BankDepositCompleteAck = 'bank-deposit-complete-ack',
  RedirectFullRegistrationAck = 'redirect-full-registration-ack',
  Authorize3DSFiserv = 'authorize-3ds-fiserv',
  Authorize3DSEnrollmentResult = 'authorize-3ds-enrollment-result',
  AccertifyDeviceIdUpdated = 'accertify-device-id-updated',
  HardwareBack = 'hardware-back',
  // oneApp -> Vue: outcome of an OpenPartnerCheckout request (uid echoes the request)
  PartnerCheckoutResult = 'partner-checkout-result',
  OpenEkycVerification = 'open-ekyc-verification',
  OpenSendMoney = 'open-send-money',
  RecipientOpenSendMoney = 'recipient-open-send-money',
}

export enum BridgeMessageToType {
  Ready = 'ready',
  ReferralCodeConsumed = 'referral-code-consumed',
  Back = 'back',
  PushConfirm = 'push-confirm',
  Login = 'login',
  Refresh = 'refresh',
  Logout = 'logout',
  Identify = 'identify',
  TrackEvent = 'track-event',
  BranchEvent = 'branch-event',
  Page = 'page',
  Native = 'native',
  LocalStorage = 'local-storage',
  HttpRequestGet = 'http-get',
  HttpRequestPut = 'http-put',
  HttpRequestPatch = 'http-patch',
  HttpRequestPost = 'http-post',
  HttpRequestDelete = 'http-delete',
  SyncData = 'sync-data',
  LoginComplete = 'login-complete',
  RegistrationComplete = 'registration-complete',
  CompleteProfileComplete = 'complete-profile-complete',
  CloseIframe = 'close-iframe',
  FetchResources = 'fetch-resources',
  OpenExternalUrl = 'open-external-url',
  Signin = 'signin',
  ValidateOTP = 'validate-otp',
  ResendOTP = 'resend-otp',
  SetLanguage = 'set-language',
  DataChanged = 'data-changed',
  OpenNativeModal = 'open-native-modal',
  BankDepositComplete = 'bank-deposit-complete',
  WelcomeCountrySelected = 'welcome-country-selected',
  RedirectFullRegistration = 'redirect-full-registration',
  OpenDebugMenu = 'open-debug-menu',
  // Vue -> oneApp: the route draws under the status bar itself (data.fullBleed)
  SetFullBleed = 'set-full-bleed',
  HardwareBackUnhandled = 'hardware-back-unhandled',
  OnboardingComplete = 'onboarding-complete',
  // Vue -> oneApp: result of an Authorize3DSFiserv request (uid echoes the request)
  Authorize3DSFiservResult = 'authorize-3ds-fiserv-result',
  // Vue -> oneApp: the full-screen auth overlay is mounted — safe to reveal the
  // iframe without flashing the underlying route (uid echoes the request)
  Authorize3DSFiservReady = 'authorize-3ds-fiserv-ready',
  // Vue -> oneApp: Songbird device-data collection finished — run the 3DS
  // enrollment order/validate natively (with oneApp's error handling) and
  // reply with Authorize3DSEnrollmentResult
  Authorize3DSEnrollment = 'authorize-3ds-enrollment',
  // Vue -> oneApp: open a partner checkout top-level in the in-app browser and
  // reply with PartnerCheckoutResult once it returns or is closed
  OpenPartnerCheckout = 'open-partner-checkout',
  // Vue -> oneApp: hand control back to native. RMT5's wallet bundle sends these,
  // so they must be enum members here, not just entries on BridgeNativeModalTarget.
  OpenEkycVerification = 'open-ekyc-verification',
  OpenSendMoney = 'open-send-money',
  RecipientOpenSendMoney = 'recipient-open-send-money',
  TransferToOneApp = 'transfer-to-one-app',
}

export const BridgeNativeModalTarget = {
  SendMoney: 'send-money',
  SendMoneyToRecipient: 'send-money-to-recipient',
  Locations: 'locations',
  Track: 'track',
  OpenEkycVerification: 'open-ekyc-verification',
  OpenSendMoney: 'open-send-money',
  RecipientOpenSendMoney: 'recipient-open-send-money',
  TransferToOneApp: 'transfer-to-one-app',
}

export const BridgeNativeType = {
  Biometrics: {
    IsAvailable: 'biometrics-available',
    Scan: 'biometrics-scan',
    Save: 'biometrics-save',
    Remove: 'biometrics-remove',
    Enable: 'biometrics-enable',
    Skip: 'biometrics-skip',
  },
  Permissions: {
    NotificationRequest: 'permissions-notification-request',
    LocationRequest: 'permissions-location-request',
  },
  /**
   * Adyen native bridge events. The Vue iframe forwards 3DS redirect
   * actions to OneApp via this channel; OneApp invokes
   * `cordova-plugin-adyen.handle3DSRedirect` (iOS: ASWebAuthenticationSession,
   * Android: Adyen3DSRedirectActivity WebView) and replies with the
   * captured `redirectResult`.
   */
  LiquidGlassBar: {
    IsAvailable: 'liquid-glass-bar-available',
    Show: 'liquid-glass-bar-show',
    Update: 'liquid-glass-bar-update',
    Hide: 'liquid-glass-bar-hide',
    SetSuppressed: 'liquid-glass-bar-set-suppressed',
  },
  Adyen: {
    Handle3DSRedirect: 'adyen-handle-3ds-redirect',
  },
  /**
   * File-system bridge events. The Vue iframe ships a base64-encoded PDF
   * (binary cannot round-trip through postMessage JSON), OneApp writes it
   * to the cache directory and opens it via cordova-plugin-file-opener2.
   * Used by the wallet account statement download where the iframe's
   * <a download> / blob URL paths are blocked by Cordova WebView.
   */
  File: {
    SharePdf: 'file-share-pdf',
    DiscardPdf: 'file-discard-pdf',
  },
  Contacts: {
    CheckPermission: 'contacts-permission-check',
    GetContacts: 'contacts-get',
  },
  AdsTracking: {
    Get: 'ads-tracking-get',
    Set: 'ads-tracking-set',
  },
  Inbox: {
    GetMessages: 'inbox-get-messages',
    MessageClick: 'inbox-message-click',
  },
  Payments: {
    AddBankAccount: 'payments-add-bank-account',
  },
  Receipt: {
    View: 'receipt-view',
  },
  Accertify: {
    GetDeviceId: 'accertify-get-device-id',
  },
  /*
  Adyen: 'adyen',
  ApiGuard: 'api-guard',
  AutoFill: 'autofill',
  FileChooser: 'file-chooser',
  Keyboard: 'keyboard',
  WebAuthenticationSession: 'web-authentication-session',
  PermitGetUserMedia: 'permit-getusermedia',
  Iterable: 'iterable',
  LocalizationStrings: 'localization-strings',
  LocalNotification: 'local-notification',
  Onfido: 'onfido',
  Plaid: 'plaid',
  Riskified: 'riskified',
  SafariViewController: 'safari-view-controller',
  SecureStorage: 'secure-storage',
  Segment: 'segment',
  Splashscreen: 'splashscreen-custom',
  Veriff: 'veriff',
  CallNumber: 'call-number',
  BarcodeScanner: 'barcodescanner',
  */
}

export const BridgeAPIType = {
  CST: 'cst',
  RMT: 'rmt',
  Logs: 'logs',
  Unknown: 'unknown',
}

export type BridgeDataChangedScope =
  | 'profile'
  | 'paymentMethods'
  | 'documentId'
  | 'notifications'
  | 'password'

export interface BridgeBackData {
  isBackPage?: 'Welcome' | 'Login' | 'ForgotPassword' | 'Registration' | 'Help' | 'CheckRates' | 'OnfidoInfoPage' | 'SignicatVerifyPage' | 'CompleteProfileInfo';
  email?: string;
  phoneNumber?: string;
  recipient?: any;
}

export const BridgeBankDepositRoute = {
  TopBanks: 'SendMoneyDeliveryTopBanks',
  BankDetails: 'SendMoneyDeliveryBankDetails',
}

export interface BridgeBankDepositEntry {
  recipientId?: number | string;
  countryTo?: string;
  currencyFrom?: string;
  currencyTo?: string;
  amountFrom?: number;
  amountTo?: number;
}

export interface BridgeBankDepositCompleteData {
  recipientId?: number | string;
  bankId?: string;
  bankName?: string;
  bankAccountFormatId?: string;
  recipient?: any;
}

export interface BridgeWelcomeCountrySelectedData {
  countryFrom?: string;
  countryTo?: string;
}

// oneApp -> Vue: payload to run the CyberSource + Songbird 3DSV2 card authorization.
// The order itself never crosses the bridge: the enrollment order/validate is
// requested back from oneApp mid-flow (Authorize3DSEnrollment), so oneApp keeps
// owning the order payload and its error handling.
// `cardinalScriptUrl`/`cardinalScriptIntegrityHash` carry oneApp's Songbird config
// so the Vue side loads the exact same SDK build (with SRI) as the native path.
export interface BridgeAuthorize3DSFiservRequest {
  accessToken: string;
  referenceId: string;
  transactionId: string | number;
  cardinalScriptUrl?: string;
  cardinalScriptIntegrityHash?: string;
}

// Vue -> oneApp: run the 3DS enrollment order/validate natively.
export interface BridgeAuthorize3DSEnrollmentRequest {
  referenceId: string;
  transactionId: string | number;
}

// oneApp -> Vue: outcome of the native enrollment validate. Mirrors what the
// Cardinal flow needs to branch: a complete payerAuthenticateResponse means
// challenge (CCA), ok without one means frictionless, anything else fails.
export interface BridgeAuthorize3DSEnrollmentResult {
  ok: boolean;
  transactionId?: string | number;
  payerAuthenticateResponse?: any;
  errors?: any[];
  error?: string;
}

// Vue -> oneApp: result of the 3DSV2 card authorization.
export interface BridgeAuthorize3DSFiservResult {
  success: boolean;
  cancelled?: boolean;
  payerAuthenticationResponse?: string;
  transactionId?: string | number;
  paTransactionId?: string;
  error?: string;
}

export type BridgePartnerCheckoutPartner = 'ding';

export type BridgePartnerCheckoutStatus = 'success' | 'cancelled' | 'failed' | 'expired' | 'closed';

// Vue -> oneApp. `token` is short-lived and single-use: it is sent apart from `url`
// so loggers can mask it by key, and is appended as the `token` query param on open.
export interface BridgePartnerCheckoutRequest {
  partner: BridgePartnerCheckoutPartner;
  url: string;
  returnUrlPrefix: string;
  token: string;
}

// oneApp -> Vue. `params` is the query of the return URL, or the params of the hosted
// page's `result` message; empty when the user closed the browser before a result.
// A `failed` result that oneApp raises itself carries `params.reason`: `load_error`
// (network/TLS error) or `load_timeout` (the hosted page never reported `ready`).
export interface BridgePartnerCheckoutResult {
  status: BridgePartnerCheckoutStatus;
  params: { [key: string]: string };
}

// Agent-referral OTP fallback (retVal 40 / OpenAgentReferral) on the registration
// /v2/validate. RMT5 lite-reg can't run the ARP recovery, so it hands off to
// native full registration with the verified OTP session and referral payload.
export interface BridgeRedirectFullRegistrationData {
  openAgentReferral?: any;
  sessionId?: string;
  phoneNumber?: string;
  phoneNumberValue?: string;
  phoneNumberCountry?: string;
  phoneNumberCountryIso?: string;
  email?: string;
  password?: string;
  country?: string;
}



// WEBPACK FOOTER //
// ./src/model/types/bridge-types.ts